import { infiniteQueryOptions, QueryClient, queryOptions } from "@tanstack/react-query";
import { ApiError, api, apiFor } from "./api";

/**
 * Every server read in one place.
 *
 * This replaces two hand-rolled patterns that were both quietly buggy: the
 * board's five parallel `useEffect` fetches guarded by an `alive` flag (which
 * cannot dedupe, cannot retry, and goes stale for up to 30s while a spectator's
 * phone is locked), and the admin's `reload()` callback threaded down through
 * fifteen panels as an `onChanged` prop.
 *
 * Reads go through `queryKeys` + the option factories below. Writes still go
 * straight through `api.*` and then invalidate: there is no mutation layer,
 * because the Worker is the only writer and the client never merges state.
 *
 * EVERY TOURNAMENT READ IS KEYED BY ITS TOURNAMENT (V2 Phase 2): the key is
 * `["t", <id>, …]`, so a phone that opened two events in one session never
 * shows one event's standings under the other's name, and one
 * `invalidateTournament(client, id)` reaches exactly one event's cache. The
 * platform reads (the registry, the superadmin surface) carry no tournament.
 *
 * Three reads deliberately live OUTSIDE this file, each for a reason:
 *  - the live game: a WebSocket plus the tap queue's own action responses
 *    (`useGameState` / `useActionQueue`) - push, not poll, and a cache layer
 *    between a scorekeeper and the score would only add a stale frame;
 *  - the gate's key exchange (`lib/session.ts`) - it MUTATES (consumes a
 *    one-shot token, sets a cookie), so caching any of it would be a bug;
 *  - `GameSetupDialog`'s open-time snapshot - the one place a fresh answer at
 *    the moment of opening beats a shared cache, because the scorekeeper is
 *    about to bind a GAME to a pitch off what they see.
 */

export const queryKeys = {
  // ---- the platform ----
  tournaments: ["tournaments"] as const,
  tournamentStats: ["tournaments", "stats"] as const,
  /** By id or slug, whichever the URL carried. */
  tournament: (key: string) => ["tournaments", key] as const,
  superadmins: ["super", "admins"] as const,
  allGrants: ["super", "grants"] as const,
  accounts: ["super", "accounts"] as const,
  invites: ["super", "invites"] as const,
  outbox: ["super", "outbox"] as const,
  superTelegram: ["super", "telegram"] as const,
  telegramConfig: ["telegram", "config"] as const,
  /** The agent's inbox (2026-09-16). */
  supportChats: ["support", "chats"] as const,
  account: ["account"] as const,
  clubs: ["clubs"] as const,
  /** By id or slug, whichever the URL carried. */
  club: (key: string) => ["clubs", key] as const,
  clubTournaments: (id: string) => ["clubs", id, "tournaments"] as const,
  teamRecord: (tid: string, teamId: string) => ["t", tid, "teams", teamId, "record"] as const,
  clubMembers: (id: string) => ["clubs", id, "members"] as const,
  clubInvites: (id: string) => ["clubs", id, "invites"] as const,
  audit: (limit: number) => ["audit", limit] as const,
  // ---- one tournament ----
  board: (tid: string) => ["t", tid, "board"] as const,
  fans: (tid: string) => ["t", tid, "fans"] as const,
  teams: (tid: string) => ["t", tid, "teams"] as const,
  schedule: (tid: string) => ["t", tid, "schedule"] as const,
  divisions: (tid: string) => ["t", tid, "divisions"] as const,
  pools: (tid: string) => ["t", tid, "pools"] as const,
  fields: (tid: string) => ["t", tid, "fields"] as const,
  stats: (tid: string) => ["t", tid, "stats"] as const,
  settings: (tid: string) => ["t", tid, "settings"] as const,
  persons: (tid: string) => ["t", tid, "persons"] as const,
  awards: (tid: string) => ["t", tid, "awards"] as const,
  publicAwards: (tid: string) => ["t", tid, "awards", "public"] as const,
  spiritPublic: (tid: string) => ["t", tid, "spirit", "public"] as const,
  spiritAwards: (tid: string) => ["t", tid, "spirit-awards"] as const,
  spiritAll: (tid: string) => ["t", tid, "spirit", "all"] as const,
  spiritTeam: (tid: string, teamId: string) => ["t", tid, "spirit", "team", teamId] as const,
  grants: (tid: string) => ["t", tid, "grants"] as const,
  tournamentInvites: (tid: string) => ["t", tid, "invites"] as const,
  regulations: (tid: string) => ["t", tid, "regulations"] as const,
} satisfies Record<string, readonly unknown[] | ((...args: never[]) => readonly unknown[])>;

/**
 * How long a spectator may look at a number before it is refetched.
 *
 * The board used to poll on a flat 30s interval, so a phone that had been in a
 * pocket showed a score up to half a minute old the instant it was unlocked.
 * `refetchOnWindowFocus` (on by default) fixes exactly that case, and the
 * interval is now the floor rather than the only mechanism.
 */
export const BOARD_POLL_MS = 30_000;

/**
 * A credential problem is not a network problem. 401/403 mean the grant is
 * missing, revoked or wrong for this surface. KeyGate says which, and
 * hammering the endpoint three more times only delays that message. A 404 is
 * settled too: the tournament in the URL does not exist, and asking again
 * will not make it.
 */
function retry(failureCount: number, error: unknown): boolean {
  if (
    error instanceof ApiError &&
    (error.status === 401 || error.status === 403 || error.status === 404)
  ) {
    return false;
  }
  return failureCount < 2;
}

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        retry,
        // Field wifi drops for seconds at a time. Back off, but not far: a
        // scorekeeper walking behind a tent should recover in seconds.
        retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 5000),
        staleTime: 10_000,
        // A stale number on screen beats a spinner: the previous value was
        // true a moment ago, and a blank card at a pitch reads as "broken".
        placeholderData: <T>(prev: T): T => prev,
      },
    },
  });
}

// ---------- the platform ----------

/**
 * The registry (V2 Phase 0). The front page polls it on the board's rhythm
 * and refetches on focus, so a phone out of a pocket shows what is on now;
 * the console reads it once and invalidates on write.
 */
export const tournamentsQuery = () =>
  queryOptions({
    queryKey: queryKeys.tournaments,
    queryFn: api.tournaments,
    staleTime: 60_000 - 5_000,
    refetchInterval: 60_000,
  });

/** The cards' numbers. Minutes of staleness: a roster grows once a season,
 *  and a result every forty minutes at most. */
export const tournamentStatsQuery = () =>
  queryOptions({
    queryKey: queryKeys.tournamentStats,
    queryFn: api.tournamentStats,
    staleTime: 5 * 60_000,
  });

/**
 * One tournament by id or slug: what the `/t/$slug` layout resolves before
 * anything under it renders. A minute of staleness is right: the name and the
 * dates change once a year, and a navigation between two pages of one event
 * must not cost a registry read each time.
 */
export const tournamentQuery = (key: string) =>
  queryOptions({
    queryKey: queryKeys.tournament(key),
    queryFn: () => api.tournament(key),
    enabled: key !== "",
    staleTime: 30_000,
    refetchInterval: (query) =>
      query.state.data?.registrationCloses ||
      query.state.data?.rosterLocks ||
      query.state.data?.endTime
        ? 30_000
        : false,
  });

export const superadminsQuery = () =>
  queryOptions({ queryKey: queryKeys.superadmins, queryFn: api.superadmins });
export const allGrantsQuery = () =>
  queryOptions({ queryKey: queryKeys.allGrants, queryFn: api.allGrants });
export const accountsQuery = () =>
  queryOptions({ queryKey: queryKeys.accounts, queryFn: api.accounts });
export const invitesQuery = () =>
  queryOptions({ queryKey: queryKeys.invites, queryFn: api.invites });

// ---------- the club library (V2 Phase 5) ----------
export const clubsQuery = () => queryOptions({ queryKey: queryKeys.clubs, queryFn: api.clubs });
export const rosterProfilesQuery = (clubId: string) =>
  queryOptions({
    queryKey: ["rosterProfiles", clubId],
    queryFn: () => api.rosterProfiles(clubId),
    enabled: clubId !== "",
    staleTime: 30_000,
  });
export const clubQuery = (key: string) =>
  queryOptions({
    queryKey: queryKeys.club(key),
    queryFn: () => api.club(key),
    enabled: key !== "",
  });
export const clubTournamentsQuery = (id: string) =>
  queryOptions({
    queryKey: queryKeys.clubTournaments(id),
    queryFn: () => api.clubTournaments(id),
    refetchInterval: 30_000,
    enabled: id !== "",
  });
export const clubMembersQuery = (id: string) =>
  queryOptions({
    queryKey: queryKeys.clubMembers(id),
    queryFn: () => api.clubMembers(id),
    enabled: id !== "",
  });
export const clubInvitesQuery = (id: string) =>
  queryOptions({
    queryKey: queryKeys.clubInvites(id),
    queryFn: () => api.clubInvites(id),
    enabled: id !== "",
  });
export const auditQuery = (limit = 200) =>
  queryOptions({ queryKey: queryKeys.audit(limit), queryFn: () => api.audit({ limit }) });

/** A team's record at one event (the public team profile asks for one per
 *  event the club entered). */
export const teamRecordQuery = (tid: string, teamId: string) =>
  queryOptions({
    queryKey: queryKeys.teamRecord(tid, teamId),
    queryFn: () => api.teamRecord(tid, teamId),
    staleTime: 60_000,
  });

// ---------- one tournament ----------

/** Reads the board makes. Polled, and refreshed the moment a phone wakes. */
const live = { staleTime: BOARD_POLL_MS - 5_000, refetchInterval: BOARD_POLL_MS } as const;

/**
 * The public board's poll, deliberately slower than `live` and ONE request.
 *
 * This deployment is on the Free plan: 100k Worker requests a day, and the
 * board is the one surface a crowd opens (shared/board.ts has the full
 * arithmetic — ten reads every 30 s supported ~15 open spectator tabs; one
 * bundle every 60 s supports one to two hundred). The score itself rides the
 * WebSocket, which costs requests only at the upgrade, so a slower poll
 * changes standings/schedule latency, never the live number. Focus refetch
 * stays on: a phone out of a pocket is current immediately.
 */
export const BOARD_BUNDLE_POLL_MS = 60_000;
export const fansQuery = (tid: string) =>
  queryOptions({
    queryKey: queryKeys.fans(tid),
    queryFn: () => apiFor(tid).fans(),
    staleTime: 10_000,
    refetchInterval: 15_000,
  });
export const boardBundleQuery = (tid: string) =>
  queryOptions({
    queryKey: queryKeys.board(tid),
    queryFn: () => apiFor(tid).board(),
    staleTime: BOARD_BUNDLE_POLL_MS - 5_000,
    refetchInterval: BOARD_BUNDLE_POLL_MS,
  });

/** Which names at this event open a profile. Asked only by the views that
 *  link names (Leaders, Awards, a game's scorers), never by the board's poll. */
export const tournamentProfilesQuery = (tid: string) =>
  queryOptions({
    queryKey: ["tournamentProfiles", tid],
    queryFn: () => apiFor(tid).profiles(),
    staleTime: 5 * 60_000,
  });

export const teamsQuery = (tid: string) =>
  queryOptions({ queryKey: queryKeys.teams(tid), queryFn: () => apiFor(tid).teams() });
export const scheduleQuery = (tid: string) =>
  queryOptions({ queryKey: queryKeys.schedule(tid), queryFn: () => apiFor(tid).schedule() });
export const divisionsQuery = (tid: string) =>
  queryOptions({ queryKey: queryKeys.divisions(tid), queryFn: () => apiFor(tid).divisions() });
export const poolsQuery = (tid: string) =>
  queryOptions({ queryKey: queryKeys.pools(tid), queryFn: () => apiFor(tid).pools() });
export const fieldsQuery = (tid: string) =>
  queryOptions({ queryKey: queryKeys.fields(tid), queryFn: () => apiFor(tid).fields() });
export const statsQuery = (tid: string) =>
  queryOptions({ queryKey: queryKeys.stats(tid), queryFn: () => apiFor(tid).stats() });
export const regulationsMetaQuery = (tid: string) =>
  queryOptions({
    queryKey: queryKeys.regulations(tid),
    queryFn: () => apiFor(tid).regulationsMeta(),
  });
export const regulationsContentQuery = (tid: string) =>
  queryOptions({
    queryKey: [...queryKeys.regulations(tid), "content"],
    queryFn: () => apiFor(tid).regulationsContent(),
  });
export const settingsQuery = (tid: string) =>
  queryOptions({ queryKey: queryKeys.settings(tid), queryFn: () => apiFor(tid).settings() });
export const personsQuery = (tid: string) =>
  queryOptions({ queryKey: queryKeys.persons(tid), queryFn: () => apiFor(tid).persons() });

export const boardTeamsQuery = (tid: string) => queryOptions({ ...teamsQuery(tid), ...live });
export const boardScheduleQuery = (tid: string) => queryOptions({ ...scheduleQuery(tid), ...live });
export const boardDivisionsQuery = (tid: string) =>
  queryOptions({ ...divisionsQuery(tid), ...live });
export const boardPoolsQuery = (tid: string) => queryOptions({ ...poolsQuery(tid), ...live });
export const boardFieldsQuery = (tid: string) => queryOptions({ ...fieldsQuery(tid), ...live });
export const boardStatsQuery = (tid: string) => queryOptions({ ...statsQuery(tid), ...live });
export const boardSettingsQuery = (tid: string) => queryOptions({ ...settingsQuery(tid), ...live });

/**
 * The three public reads whose OUTPUT depends on a tournament setting.
 *
 * `enabled` here is a COST optimisation and never the gate: the publish
 * decision is made inside TournamentDO, so a spectator who calls the endpoint
 * directly still gets an empty payload. Skipping the request only avoids one
 * round trip per poll per spectator at the events that publish nothing,
 * which is the default, and therefore most of them.
 */
export const publicSpiritQuery = (tid: string, enabled: boolean) =>
  queryOptions({
    ...live,
    queryKey: queryKeys.spiritPublic(tid),
    queryFn: () => apiFor(tid).spiritPublic(),
    enabled,
  });
export const publicAwardsQuery = (tid: string, enabled: boolean) =>
  queryOptions({
    ...live,
    queryKey: queryKeys.publicAwards(tid),
    queryFn: () => apiFor(tid).publicAwards(),
    enabled,
  });
export const boardPersonsQuery = (tid: string, enabled: boolean) =>
  queryOptions({
    ...live,
    queryKey: queryKeys.persons(tid),
    queryFn: () => apiFor(tid).persons(),
    enabled,
  });

export const awardsQuery = (tid: string) =>
  queryOptions({ queryKey: queryKeys.awards(tid), queryFn: () => apiFor(tid).awards() });
export const spiritAllQuery = (tid: string) =>
  queryOptions({ queryKey: queryKeys.spiritAll(tid), queryFn: () => apiFor(tid).spiritAll() });
export const spiritTeamQuery = (tid: string, teamId: string) =>
  queryOptions({
    queryKey: queryKeys.spiritTeam(tid, teamId),
    queryFn: () => apiFor(tid).spiritTeam(teamId),
    enabled: teamId !== "",
  });
export const grantsQuery = (tid: string) =>
  queryOptions({ queryKey: queryKeys.grants(tid), queryFn: () => apiFor(tid).grants() });
export const tournamentInvitesQuery = (tid: string) =>
  queryOptions({
    queryKey: queryKeys.tournamentInvites(tid),
    queryFn: () => apiFor(tid).invites(),
  });

/**
 * The tournament nouns every admin panel reads.
 *
 * Almost every write touches more than one of them: deleting a division
 * re-homes its teams and its pools, a generated bracket rewrites the schedule,
 * so the panels invalidate this whole group rather than guessing which. It is
 * a handful of cached GETs, not a round trip each: Query dedupes and only
 * refetches what something is actually observing.
 *
 * `settings` BELONGS HERE and was missing, which was a bug with a very
 * particular shape. Every settings form does the same three things after a
 * successful PUT — invalidate, drop its local draft, report "Saved" — and with
 * this key absent the second one dropped the draft back onto a cache nothing
 * had refreshed. The form visibly reverted to the old numbers, stayed reverted
 * because `settingsQuery` has no poll, and came right on a page reload. It hit
 * every settings surface: durations, spirit, stats and the tournament's
 * default language.
 *
 * It is also the key the durations now travel on, and the schedule tab
 * measures its warnings with those, so a stale one is not just a form showing
 * the wrong number — it is the checker answering about a different tournament.
 */
export function tournamentKeys(tid: string): readonly (readonly string[])[] {
  return [
    ["super", "operations"],
    // The board bundle repeats most of the keys below; invalidating it here is
    // what keeps a board tab open in the ORGANISER's own browser current the
    // moment they edit, same as every other read.
    queryKeys.board(tid),
    queryKeys.teams(tid),
    queryKeys.schedule(tid),
    queryKeys.divisions(tid),
    queryKeys.pools(tid),
    queryKeys.fields(tid),
    queryKeys.stats(tid),
    queryKeys.persons(tid),
    queryKeys.settings(tid),
  ];
}

export function invalidateTournament(client: QueryClient, tid: string): void {
  for (const key of tournamentKeys(tid)) void client.invalidateQueries({ queryKey: key });
}

/** Whether anything leaves this deployment (V3 Phase 1): a line on the
 *  People view. A deploy-time fact, so minutes of staleness are fine. */
export const outboxQuery = () =>
  queryOptions({ queryKey: queryKeys.outbox, queryFn: api.outbox, staleTime: 5 * 60_000 });

/** The super's Telegram card: refetched after the webhook is set. */
export const superTelegramQuery = () =>
  queryOptions({
    queryKey: queryKeys.superTelegram,
    queryFn: api.superTelegram,
    staleTime: 60_000,
  });

/** Whether the Telegram door is offered, and the bot for deep links. Read
 *  by the login page and the issued-link dialog; a deploy-time fact. */
export const telegramConfigQuery = () =>
  queryOptions({
    queryKey: queryKeys.telegramConfig,
    queryFn: api.telegramConfig,
    staleTime: 10 * 60_000,
  });

/** The account's own record, for `/account`. */
export const accountQuery = () =>
  queryOptions({ queryKey: queryKeys.account, queryFn: api.account, staleTime: 30_000 });

/** The agent's inbox: polled while it is on screen, a chat's own page
 *  holds a socket instead. */
export const supportChatsQuery = () =>
  queryOptions({
    queryKey: queryKeys.supportChats,
    queryFn: api.support.list,
    staleTime: 10_000,
    refetchInterval: 20_000,
  });

export const playersQuery = (q = "", club?: string) =>
  queryOptions({
    queryKey: ["players", q, club],
    queryFn: () => api.players(q, club),
    staleTime: 30_000,
  });
export const playerDirectoryQuery = (q = "", club?: string) =>
  infiniteQueryOptions({
    queryKey: ["players", "directory", q, club],
    queryFn: ({ pageParam }) => api.playerDirectory(q, club, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (page) => page.nextCursor ?? undefined,
    staleTime: 30_000,
  });
export const playerQuery = (id: string) =>
  queryOptions({ queryKey: ["player", id], queryFn: () => api.player(id), staleTime: 30_000 });
export const personalEventsQuery = () =>
  queryOptions({
    queryKey: ["account", "events"],
    queryFn: api.personalEvents,
    refetchInterval: 30_000,
  });

/** The front page's card (2026-09-16): the person's squads at current events,
 *  each with its live game, fresh result or next kickoff. Polled while shown. */
export function accountNextQuery() {
  return queryOptions({ queryKey: ["account", "next"] as const, queryFn: api.accountNext });
}

/** What a tournament is playing right now, for the same card when the reader
 *  has no squad there. */
export function tournamentNowQuery(tid: string) {
  return queryOptions({
    queryKey: ["tournaments", tid, "now"] as const,
    queryFn: () => apiFor(tid).now(),
  });
}

export const spiritAwardsQuery = (tid: string) =>
  queryOptions({
    queryKey: queryKeys.spiritAwards(tid),
    queryFn: () => apiFor(tid).spiritAwards(),
  });
